import { useEffect, useMemo, useState } from "react";
import { arrayOf, func, number, oneOfType, shape, string } from "prop-types";
import { observer } from "mobx-react";
import { PriceFormats } from "@repo/utils";
import { parsePrice, roundFloat } from "@repo/utils";
import { CELL_HEIGHTS } from "@utils";
import { difference, intersection } from "lodash";
import { useEstimateEditorSettings, useEstimateStructure } from "@tools";
import { Button } from "@components";
import { Collapse, Grid, Tooltip } from "@material-ui/core";
import { BillingChartBarInner } from "./BillingChartBarInner";
import { BillingChartBarTooltipContent } from "./BillingChartBarTooltipContent";
import useStyle from "./BillingChartBar.style";
import classnames from "classnames";

export const BillingChartBar =  observer(({
  values,
  onOpen,
}) => {
  const classes = useStyle();
  const structure = useEstimateStructure();
  const { blockExpansions } = useEstimateEditorSettings();
  
  const { timelineBillingScale, settings } = structure;
  const { currencyObj, modifier, roundPrice, timeModifier, priceFormat } = settings;
  const { topValue } = timelineBillingScale;
  
  const [ opened, setOpened ] = useState(false);
  const [ workTypeIds, setWorkTypeIds ] = useState([]);
  
  useEffect(() => {
    const newIds = values.map(v => v.id);
    if(newIds?.length !== workTypeIds.length || intersection(newIds, workTypeIds).length !== workTypeIds.length) {
      if(opened) {
        onOpen(true, difference(newIds, workTypeIds));
        onOpen(false, difference(workTypeIds, newIds));
      }
      setWorkTypeIds(newIds);
    }
    // setWorkTypeIds(newIds);
  }, [values])
  
  const sortedValues = useMemo(() => (
    values.sort((a,b) => a.value > b.value ? 1 : -1)
  ), [values])
  
  const combinedValue = useMemo(() => (
    values.reduce((t, v) => t + v.value, 0)
  ), [values]);
  
  const heights = useMemo(() => (
    sortedValues.map((v) => (
      topValue
        ? (4 * CELL_HEIGHTS.XXLARGE - 8) * Number((v.value / topValue).toPrecision(12))
        : 0
    ))
  ), [sortedValues, topValue])
  
  const height = useMemo(() => (
    heights.reduce((t, v) => t + v, 0)
  ), [heights])
  
  const handleOpen = () => {
    if(blockExpansions) return;
    setOpened(!opened);
    onOpen(!opened, values.map(v => v.id));
  }
  
  return (
    <Grid
      item container
      direction="column"
      className={classnames(classes.container)}
    >
      {
        combinedValue > 0 &&
        <Grid
          item container
          justifyContent="center"
          className="color-primary text-xs mb-2 text-nowrap"
        >
          {currencyObj.symbolStart || ""}
          {parsePrice(roundFloat(combinedValue, roundPrice ? 3 : 0, !roundPrice), priceFormat === PriceFormats.AMERICAN)}
          { currencyObj.symbolEnd ? " " + currencyObj.symbolEnd : undefined }
        </Grid>
      }
      <Tooltip
        placement="top"
        title={(
          opened
            ? ""
            : <BillingChartBarTooltipContent
              values={values}
              roundPrice={roundPrice}
              currencyObj={currencyObj}
              americanFormat={priceFormat === PriceFormats.AMERICAN}
            />
        )}
      >
        <Button
          onClick={handleOpen}
          style={{ height }}
          color="primary"
          variant="contained"
          className={classnames( "p-0 overflow-hidden", classes.block, opened && classes.blockOpened )}
        >
          <Collapse
            in={opened}
            mountOnEnter
            unmountOnExit
            className="w-full"
          >
            <Grid item container direction="column">
              {
                sortedValues
                  .map((v, i) => (
                    <BillingChartBarInner
                      key={v.name}
                      { ...v }
                      hours={roundFloat(v.hours / modifier)}
                      height={heights[i]}
                      currencyObj={currencyObj}
                      roundPrice={roundPrice}
                      timeModifier={timeModifier}
                      americanFormat={priceFormat === PriceFormats.AMERICAN}
                    />
                  ))
              }
            </Grid>
          </Collapse>
        </Button>
      </Tooltip>
    </Grid>
  );
});

BillingChartBar.propTypes = {
  onOpen: func.isRequired,
  values: arrayOf(shape({
    id: oneOfType([number, string]).isRequired,
    value: number.isRequired,
    hours: number.isRequired,
    name: string.isRequired,
    color: string,
  })),
};
